import { LabelSelector } from "@/components/PredictionMarket/LabelSelector";
import { MarketCardListItem } from "@/components/PredictionMarket/MarketCardList/MarketCardListItem";
import { MultipleChoices } from "@/components/PredictionMarket/MarketCardList/MultipleChoices";
import { SearchInput } from "@/components/PredictionMarket/SearchInput";
import { SelectSortPredictionMarket } from "@/components/PredictionMarket/SortPredictionMarket";
import { SubLabelSelector } from "@/components/PredictionMarket/SubLabelSelector";
import { PromotionBanners } from "@/components/homepage/PromotionBanners";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { useGetActiveLabels } from "@/hooks/predictionMarket/useGetActiveLabels";
import { useGetPublicPredictionMarkets } from "@/hooks/predictionMarket/useGetPublicPredictionMarkets";
import {
  type LabelData,
  type SubLabelData,
  getPredictionMarketLabelQueries,
  getQueryFilterSpecification,
} from "@/hooks/predictionMarket/usePredictionMarketLabels";
import { useGetActiveLeaderboardCycle } from "@/hooks/useGetActiveLeaderboardCycle";
import { useGetMyLeaderboardRank } from "@/hooks/useGetMyLeaderboardRank";
import { useHandleError } from "@/hooks/useHandleError";
import { useInfiniteScroll } from "@/hooks/useInfiniteScroll";
import { useDisplayLegalNotice, useFirstVisitStore, useHomePageStore } from "@/store";
import { useAuthStore } from "@/store/auth.store";
import type {
  PredictionMarketChoiceWithProbability,
  PredictionMarketWithProbability,
} from "@roman/shared/types/PredictionMarket";
import { PredictionMarketType, getPredictionMarketType } from "@roman/shared/utils/predictionMarket/utils";
import { useEffect, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { EntryPopup } from "../../components/PredictionMarket/EntryPopup";
import { LegalNoticeModal } from "../Home/LegalNoticeModal";
import { shouldDisplayLegalNotice } from "../Home/utils";
import { LeaderboardInfoCard } from "../Leaderboard/components/LeaderboardInfoCard";
import { DEFAULT_LABELS_PREDICTION_MARKET } from "./constant";

export const PredictionMarketTop = () => {
  const selectedLabel = useHomePageStore((state) => state.selectedLabel);
  const setSelectedLabel = useHomePageStore((state) => state.setSelectedLabel);

  const selectedSubLabel = useHomePageStore((state) => state.selectedSubLabel);
  const setSelectedSubLabel = useHomePageStore((state) => state.setSelectedSubLabel);

  const selectedSortPredictionMarketType = useHomePageStore((state) => state.selectedSortPredictionMarketType);
  const setSelectedSortPredictionMarketType = useHomePageStore((state) => state.setSelectedSortPredictionMarketType);

  const { queryLabel, orderBy, querySubLabel } = getPredictionMarketLabelQueries(selectedLabel, selectedSubLabel);
  const [search, setSearch] = useState("");
  const fetchUser = useAuthStore((state) => state.fetchAndSetUser);
  const navigate = useNavigate();

  const [subLabels, setSubLabels] = useState<SubLabelData[]>([]);

  const {
    predictionMarkets,
    perPage,
    isValidating,
    isLoading,
    mutate: getPublicPredictionMarkets,
    triggerRef,
    triggerAtPercentage,
  } = useInfiniteScroll(useGetPublicPredictionMarkets, {
    label: queryLabel,
    subLabel: querySubLabel,
    orderBy,
    search,
    type: getQueryFilterSpecification(selectedSortPredictionMarketType),
    swrOptions: {
      dedupingInterval: 60000, // 60 seconds cache
    },
  });

  const { activeLabels, isLoading: isLoadingActiveLabels } = useGetActiveLabels();
  const [isEntrySheetOpen, setIsEntrySheetOpen] = useState(false);
  const [selectedPredictionMarket, setSelectedPredictionMarket] = useState<PredictionMarketWithProbability | null>(
    null,
  );
  const [selectedChoice, setSelectedChoice] = useState<PredictionMarketChoiceWithProbability | null>(null);

  const { myRank } = useGetMyLeaderboardRank({
    swrOptions: {
      onError: useHandleError("自分の順位の取得に失敗しました"),
    },
  });

  const { data: result, isLoading: isLoadingLeaderboardCycle } = useGetActiveLeaderboardCycle({
    swrOptions: {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      onError: useHandleError("アクティブなリーダーボードサイクルの取得に失敗しました"),
    },
  });

  const {
    isPreventedNotice,
    setShowLegalNotice,
    lastLegalNoticeDisplayTime,
    setLastLegalNoticeDisplayTime,
    showLegalNotice,
  } = useDisplayLegalNotice();
  const hasVisitedPMVersion = useFirstVisitStore((state) => state.hasVisitedPMVersion);

  useEffect(() => {
    const now = Date.now();
    if (shouldDisplayLegalNotice(lastLegalNoticeDisplayTime, now)) {
      setShowLegalNotice(true);
      setLastLegalNoticeDisplayTime(now);
    }
  }, [lastLegalNoticeDisplayTime, setShowLegalNotice, setLastLegalNoticeDisplayTime]);

  useEffect(() => {
    if (!Array.isArray(activeLabels)) {
      // Add "すべて" to the beginning of each subLabel array in activeLabels
      // Collect all unique sub-labels from all activeLabels
      let allSubLabels: string[] = [];
      if (activeLabels && typeof activeLabels === "object") {
        allSubLabels = [
          ...new Set(
            Object.values(activeLabels)
              .flat()
              .filter((l) => l !== "すべて"),
          ),
        ];
      }

      const processedActiveLabels: Record<string, string[]> = {
        人気: ["すべて", ...allSubLabels],
        New: ["すべて", ...allSubLabels],
        ...(activeLabels
          ? Object.fromEntries(
              Object.entries(activeLabels).map(([key, subLabels]) => [
                key,
                ["すべて", ...subLabels.filter((l) => l !== "すべて")],
              ]),
            )
          : {}),
      };
      const processedSubLabels = processedActiveLabels[selectedLabel].map((label: string) => ({
        id: `${selectedLabel}-${label}`,
        label,
      }));
      setSubLabels(processedSubLabels);
      const foundSubLabel =
        processedSubLabels.find((subLabel) => subLabel.id === selectedSubLabel?.id) || processedSubLabels[0];

      // Only update if the sub-label actually changes
      if (selectedSubLabel.id !== foundSubLabel.id) {
        setSelectedSubLabel(foundSubLabel);
      }
    }
  }, [activeLabels, selectedLabel, selectedSubLabel, setSelectedSubLabel]);

  const labels: LabelData[] = [...DEFAULT_LABELS_PREDICTION_MARKET, ...(activeLabels ? Object.keys(activeLabels) : [])];

  const handleCloseEntrySheet = () => {
    setIsEntrySheetOpen(false);
    getPublicPredictionMarkets();
  };

  const shouldRedirectToTutorial = !hasVisitedPMVersion && !showLegalNotice && lastLegalNoticeDisplayTime; // Make sure the user has seen the legal notice before redirecting to the tutorial
  if (shouldRedirectToTutorial) {
    return <Navigate to="/tutorial" />;
  }

  return (
    <div className="w-full bg-white">
      <PromotionBanners />

      {result?.leaderboardCycle && (
        <div className="cursor-pointer" onClick={() => navigate("/leaderboard")}>
          <LeaderboardInfoCard
            freePointsToNextRank={myRank?.freePointsToNextRank ?? "0"}
            leaderboardCycle={result.leaderboardCycle}
            currentRank={myRank?.rank}
            potentialRedeemablePoints={myRank?.potentialRedeemablePoints}
            isShowPointGap={true}
            isShowRulesButton={false}
          />
        </div>
      )}
      <LabelSelector
        labels={labels}
        selectedLabel={selectedLabel}
        onLabelSelect={(label: LabelData) => {
          setSelectedLabel(label);
        }}
      />

      <div className="mt-5 mx-5 flex flex-row items-center justify-between gap-[9px]">
        <SearchInput setSearch={setSearch} className="flex-1" />
        <SelectSortPredictionMarket
          className="h-10"
          selectedSortPredictionMarketType={selectedSortPredictionMarketType}
          setSelectedSortPredictionMarketType={setSelectedSortPredictionMarketType}
        />
      </div>

      {subLabels && subLabels.length > 0 && (
        <SubLabelSelector
          className="mt-5 mx-5"
          subLabels={subLabels}
          selectedSubLabel={selectedSubLabel}
          onSubLabelSelect={setSelectedSubLabel}
        />
      )}

      <div className="p-5 grid gap-5 w-full grid-cols-1 sm:grid-cols-2">
        {(isLoadingActiveLabels || isLoadingLeaderboardCycle || isLoading) &&
          Array.from({ length: 6 }).map((_, index) => <LoadingSkeleton key={index} />)}

        {predictionMarkets.map((predictionMarket, index) => {
          const predictionMarketType = getPredictionMarketType(
            predictionMarket.predictionMarketChoices.map((c: { name: string }) => ({ name: c.name })),
          );
          const fetchPoint = predictionMarkets.length - Math.floor(perPage * triggerAtPercentage);
          const isReachedFetchPoint = index + 1 === fetchPoint;

          return (
            <div key={predictionMarket.id}>
              <MarketCardListItem
                market={predictionMarket}
                choiceComponent={
                  <MultipleChoices
                    isScrollable={false}
                    choices={predictionMarket.predictionMarketChoices.sort((a, b) => b.probability - a.probability)}
                    onButtonClick={(choiceId) => {
                      setIsEntrySheetOpen(true);
                      setSelectedPredictionMarket(predictionMarket);
                      setSelectedChoice(
                        predictionMarket.predictionMarketChoices.find((c) => c.id === choiceId) ??
                          predictionMarket.predictionMarketChoices[0],
                      );
                    }}
                    outlined={predictionMarketType === PredictionMarketType.YESNO}
                    isShowProbability={false}
                  />
                }
              />
              {!isValidating && isReachedFetchPoint && <div ref={triggerRef} />}
            </div>
          );
        })}

        <Dialog open={isEntrySheetOpen} onOpenChange={setIsEntrySheetOpen}>
          <DialogContent className="p-0 [&>button]:hidden gap-0 border-0 overflow-hidden rounded-lg w-[360px]">
            {selectedPredictionMarket && selectedChoice && (
              <EntryPopup
                predictionMarket={selectedPredictionMarket}
                selectedChoice={selectedChoice}
                onClose={() => {
                  fetchUser();
                  handleCloseEntrySheet();
                }}
              />
            )}
          </DialogContent>
        </Dialog>
      </div>

      {!isPreventedNotice && <LegalNoticeModal />}
    </div>
  );
};

const LoadingSkeleton = () => {
  return (
    <Skeleton className="rounded-xl overflow-hidden">
      <Skeleton className="h-[103px] w-full rounded-none" />

      <div className="px-[15px] pt-2.5 pb-5 flex flex-col gap-2">
        <div className="flex justify-between items-center">
          <Skeleton className="h-6 w-14" />
          <Skeleton className="h-6 w-6" />
        </div>
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-16 w-full" />
        <div className="flex justify-between items-center">
          <Skeleton className="h-6 w-20" />
          <Skeleton className="h-6 w-24" />
        </div>
      </div>
    </Skeleton>
  );
};
